import { FC, useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  Dialog,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { LoadingButton } from '@mui/lab';
import CancelIcon from '@mui/icons-material/Cancel';
import QueueIcon from '@mui/icons-material/Queue';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';
import EmailModal from '../../../SharedComponents/Header/components/EmailModal/EmailModal';
import { getEmailGroups, getEmails, sendReportEmail } from '../../../Services/LondunarkerfiAPIService';
import Email from '../../../Models/EmailModels/Email';
import EmailGroup from '../../../Models/EmailModels/EmailGroup';
import User from '../../../Models/UserModels/User';
import { MobilePaperComponent, PaperComponent } from '../../../SharedComponents/Paper/CustomPaper';
import { logError } from '../../../Helpers/LogError';
import EmailSendReport from '../../../Models/EmailModels/EmailSendReport';
import { FilterPayload } from './Filters/FilterDefinition';

interface SendReportModalProps {
  user: User;
  open: boolean;
  toggleOpen: () => void;
  showSnackbar: (message: string, severity: string) => void;
  selectedReport: { id: string; title: string; reportDate?: string; description?: string };
  isMobileView: boolean;
  filterModel: FilterPayload | { logic: "and", clauses: [] };
  groupBy: string[] | undefined;
  dateFrom: string;
  dateTo: string;
  traceId?: number;
}

type RecipientOption = {
  type: 'email' | 'group';
  value: string;
  label: string;
  subLabel?: string;
  emails?: string[];
};

const SendReportModal: FC<SendReportModalProps> = (props) => {
  const [emails, setEmails] = useState<Email[]>([]);
  const [emailGroups, setEmailGroups] = useState<EmailGroup[]>([]);
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const [selectedEmailGroups, setSelectedEmailGroups] = useState<string[]>([]);
  const [subject, setSubject] = useState<string>('');
  const [content, setContent] = useState<string>('');
  const [sendReceiptToSender, setSendReceiptToSender] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const [openEmailControl, setOpenEmailControl] = useState<boolean>(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (props.user && props.open) {
          const fetchedEmails = await getEmails(props.user.userPickedHarbour);
          setEmails(fetchedEmails.sort((a, b) => a.email.localeCompare(b.email)));

          const fetchedEmailGroups = await getEmailGroups(props.user.userPickedHarbour);
          const filteredEmailGroups = fetchedEmailGroups.filter(group => group.emails.length > 0);
          setEmailGroups(filteredEmailGroups.sort((a, b) => a.description.localeCompare(b.description)));
        }
      } catch (error) {
        logError(error);
      }
    };
    fetchData();
  }, [openEmailControl, props.user, props.open]);

  useEffect(() => {
    setSubject(
      (props.selectedReport?.title ? props.selectedReport.title : 'Skýrsla') +
      (
        props.dateFrom && props.dateTo
          ? ` - ${props.dateFrom} til ${props.dateTo}`
          : (props.selectedReport?.reportDate ? ` - ${props.selectedReport.reportDate}` : '')
      )
    );
    setContent(
      (props.selectedReport?.description ? props.selectedReport.description : '')
    );
  }, [props.dateFrom, props.dateTo, props.selectedReport]);

  const emailOptions = useMemo<RecipientOption[]>(() => emails.map(email => ({
    type: 'email' as const,
    value: email.email,
    label: email.email,
    subLabel: email.comment,
  })), [emails]);

  const groupOptions = useMemo<RecipientOption[]>(() => emailGroups.map(group => ({
    type: 'group' as const,
    value: group.description,
    label: group.description,
    subLabel: group.contact?.name,
    emails: group.emails.map(e => e.email),
  })), [emailGroups]);

  const selectedEmailOptions = useMemo<RecipientOption[]>(() =>
    emailOptions.filter(option => selectedEmails.includes(option.value)),
  [emailOptions, selectedEmails]);

  const selectedGroupOptions = useMemo<RecipientOption[]>(() =>
    groupOptions.filter(option => selectedEmailGroups.includes(option.value)),
  [groupOptions, selectedEmailGroups]);

  const emailFilterOptions = createFilterOptions<RecipientOption>({
    stringify: (option) => `${option.label} ${option.subLabel ?? ''} ${(option.emails ?? []).join(' ')}`,
  });

  const groupFilterOptions = createFilterOptions<RecipientOption>({
    stringify: (option) => `${option.label} ${option.subLabel ?? ''} ${(option.emails ?? []).join(' ')}`,
  });

  const handleEmailsChange = (_: unknown, values: RecipientOption[]) => {
    setSelectedEmails(values.map(v => v.value));
  };

  const handleGroupsChange = (_: unknown, values: RecipientOption[]) => {
    setSelectedEmailGroups(values.map(v => v.value));
  };

  const handleClose = () => {
    setSelectedEmailGroups([]);
    setSelectedEmails([]);
    props.toggleOpen();
  };

  const handleSubjectChange = (nextSubject: string) => setSubject(nextSubject);
  const handleContentChange = (nextContent: string) => setContent(nextContent);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const selectedEmailObjects: Email[] = [];

      selectedEmailGroups.forEach(groupDescription => {
        const group = emailGroups.find(g => g.description === groupDescription);
        if (group) {
          selectedEmailObjects.push(...group.emails);
        }
      });

      selectedEmails.forEach(email => {
        if (!selectedEmailObjects.find(e => e.email === email)) {
          selectedEmailObjects.push({ email });
        }
      });

      const uniqueEmailAddresses = Array.from(new Set(selectedEmailObjects.map(email => email.email)));
      if (sendReceiptToSender) {
        uniqueEmailAddresses.push(props.user.email);
      }
      const body: EmailSendReport = {
        reportType: props.selectedReport.id,
        filterModel: props.filterModel,
        groupBy: props.groupBy,
        emailAddress: uniqueEmailAddresses,
        replyTo: props.user.email,
        subject: subject,
        body: content,
        harbourId: props.user.userPickedHarbour,
        dateFrom: props.dateFrom,
        dateTo: props.dateTo,
        traceId: props.traceId
      };
      await sendReportEmail(body);
      props.showSnackbar('Skýrsla send', 'success');
      handleClose();
      setLoading(false);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      setLoading(false);
      if (error.response && error.response.data && typeof error.response.data !== 'object') {
        props.showSnackbar(error.response.data, 'error');
      } else {
        logError(error);
        props.showSnackbar('Óvænt villa kom upp', 'error');
      }
    }
  };

  const toggleEmailControlModal = () => setOpenEmailControl(!openEmailControl);
  const toggleSendReceiptToSender = () => setSendReceiptToSender(!sendReceiptToSender);

  return (
    <Dialog
      open={props.open}
      onClose={handleClose}
      PaperComponent={props.isMobileView ? MobilePaperComponent : PaperComponent}
      aria-labelledby="draggable-dialog-title"
      maxWidth="sm"
    >
      <EmailModal
        open={openEmailControl}
        toggleOpen={toggleEmailControlModal}
        showSnackbar={props.showSnackbar}
        user={props.user}
      />
      <div id="draggable-dialog-title" style={{ width: '100%' }}>
        <Typography style={{ fontWeight: 'bold', lineHeight: '1.5em', fontSize: '1.5em', cursor: 'move' }}>
          Senda skýrslu (PDF)
        </Typography>
      </div>
      <div style={{ position: 'absolute', top: 0, right: 0 }}>
        <Tooltip title="Stjórna netföngum">
          <IconButton color='primary' size='large' onClick={toggleEmailControlModal}>
            <QueueIcon />
          </IconButton>
        </Tooltip>
        <IconButton onClick={handleClose}>
          <CancelIcon fontSize='large' />
        </IconButton>
      </div>
      <div>
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={emailOptions}
          value={selectedEmailOptions}
          getOptionLabel={(option) => option.label}
          filterOptions={emailFilterOptions}
          onChange={handleEmailsChange}
          renderOption={(props, option, { selected }) => (
            <li {...props} key={`${option.type}-${option.value}`} style={{ paddingLeft: 8, paddingRight: 8 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <PersonIcon fontSize="small" />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontWeight={selected ? 'bold' : 'regular'} noWrap>
                    {option.label}
                  </Typography>
                  {option.subLabel && (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {option.subLabel}
                    </Typography>
                  )}
                  {option.type === 'group' && option.emails && (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {option.emails.join(', ')}
                    </Typography>
                  )}
                </Box>
                <Checkbox checked={selected} />
              </Box>
            </li>
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={`${option.type}-${option.value}`}
                icon={<PersonIcon />}
                label={option.label}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Veldu viðtakendur"
              placeholder="Leita að netfangi"
            />
          )}
          disabled={loading}
          fullWidth
        />
      </div>
      <div style={{ marginTop: '1em' }}>
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={groupOptions}
          value={selectedGroupOptions}
          getOptionLabel={(option) => option.label}
          filterOptions={groupFilterOptions}
          onChange={handleGroupsChange}
          renderOption={(props, option, { selected }) => (
            <li {...props} key={`${option.type}-${option.value}`} style={{ paddingLeft: 8, paddingRight: 8 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <GroupsIcon fontSize="small" />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontWeight={selected ? 'bold' : 'regular'} noWrap>
                    {option.label}
                  </Typography>
                  {option.subLabel && (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {option.subLabel}
                    </Typography>
                  )}
                  {option.emails && (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {option.emails.join(', ')}
                    </Typography>
                  )}
                </Box>
                <Checkbox checked={selected} />
              </Box>
            </li>
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={`${option.type}-${option.value}`}
                icon={<GroupsIcon />}
                label={option.label}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Veldu hópa"
              placeholder="Leita að hóp"
            />
          )}
          disabled={loading}
          fullWidth
        />
      </div>
      <TextField
        label="Efni"
        value={subject}
        onChange={(event) => handleSubjectChange(event.target.value)}
        sx={{ mt: 2 }}
      />
      <TextField
        label="Meginmál"
        multiline
        rows={4}
        value={content}
        onChange={(event) => handleContentChange(event.target.value)}
        sx={{ mt: 2 }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.stopPropagation();
          }
        }}
      />
      <FormGroup>
        <FormControlLabel
          control={
            <Checkbox
              checked={sendReceiptToSender}
              onChange={toggleSendReceiptToSender}
            />
          }
          label="Senda afrit á sendanda"
        />
      </FormGroup>
      <FormControl component="fieldset" sx={{ mt: 2 }}>
      </FormControl>
      <LoadingButton
        ref={submitButtonRef}
        size="small"
        onClick={handleSubmit}
        loading={loading}
        disabled={loading || (selectedEmails.length === 0 && selectedEmailGroups.length === 0) || subject === '' || content === ''}
        variant="contained"
      >
        <span>Senda skýrslu</span>
      </LoadingButton>
    </Dialog >
  );
};

export default SendReportModal;
